import { DEFAULT_EXPECTED_RETURN_PCT } from '../data/countryInvestmentCatalog';
import type { CoreAccount, CoreHousehold } from '../types/financial';
import type { OnboardingDraft } from '../types/onboarding';
import type { SyncedPortfolioAccount } from '../services/portfolioSyncService';
import { seedMissingInvestmentObservations } from './balanceObservations';
import {
  isPortfolioInvestmentAccount,
  isSpendLikePortfolioAccount,
} from './portfolioAccountCategory';

/** Cap for multi-decade charts — screenshot YTD can be 30%+ and blows up projections. */
export const MAX_LONG_TERM_RETURN_PCT = 12;

/** Normalize a return % for 10–20y projections (not short-term YTD display). */
export function longTermReturnPct(raw?: number | null): number {
  if (raw == null || !Number.isFinite(raw) || raw <= 0) {
    return DEFAULT_EXPECTED_RETURN_PCT;
  }
  return Math.min(raw, MAX_LONG_TERM_RETURN_PCT);
}

/**
 * Replace balance-sheet assets with Mongo-synced portfolio rows.
 * When a snapshot exists, assets are network-only (no local/sample/draft mix-in).
 * Liabilities stay local (home loan planner) until those are fully server-owned.
 */
export function overlaySyncedPortfolioOnHousehold(
  household: CoreHousehold,
  accounts: SyncedPortfolioAccount[],
  hasSnapshot: boolean,
): CoreHousehold {
  if (!hasSnapshot) return household;

  const liabilities = household.accounts.filter(
    (a) => a.kind === 'liability' && !isSampleAccountId(a.id),
  );
  const holdings = accounts.filter((account) => !isSpendLikePortfolioAccount(account));
  const syncedAssets: CoreAccount[] = holdings.map((account, index) => {
    const isInvestment = isPortfolioInvestmentAccount(account);
    return {
      id:
        account.accountId?.trim() ||
        (isInvestment ? `synced_invest_${index}` : `synced_other_${index}`),
      kind: 'asset' as const,
      type: account.type || (isInvestment ? 'investment' : 'other'),
      label: account.label,
      balance: Math.max(0, account.balance),
      isInvestment,
      // Keep extracted/edited % on the balance sheet; derive clamps for projections.
      expectedReturnPct: isInvestment ? account.expectedReturnPct : undefined,
      // Imports do not carry contribution plans — avoid sample $900/mo leftovers.
    };
  });

  return {
    ...household,
    accounts: [...syncedAssets, ...liabilities],
  };
}

/** True when the draft has at least one positive balance-sheet row. */
export function draftHasMeaningfulAssets(draft: OnboardingDraft): boolean {
  return draft.assets.some((a) => a.value > 0) || draft.liabilities.some((l) => l.balance > 0);
}

/** Convert onboarding draft rows into CoreAccount entities for derive / persist. */
export function accountsFromDraft(draft: OnboardingDraft): CoreAccount[] {
  const assets: CoreAccount[] = draft.assets
    .filter((asset) => asset.value > 0)
    .map((asset, index) => ({
      id: `draft_asset_${asset.type}_${index}`,
      kind: 'asset' as const,
      type: asset.type,
      label: asset.label,
      balance: asset.value,
      isInvestment: asset.isInvestment,
      expectedReturnPct: asset.isInvestment
        ? (asset.expectedReturnPct ?? DEFAULT_EXPECTED_RETURN_PCT)
        : asset.expectedReturnPct,
    }));

  const liabilities: CoreAccount[] = draft.liabilities
    .filter((liability) => liability.balance > 0)
    .map((liability, index) => ({
      id: `draft_liab_${liability.type}_${index}`,
      kind: 'liability' as const,
      type: liability.type,
      label: liability.label,
      balance: liability.balance,
      interestRatePct: liability.interestRatePct,
      monthlyPayment: liability.monthlyPayment,
    }));

  return [...assets, ...liabilities];
}

/** Bundled sampleHousehold.json ids — never treat as the user's portfolio. */
export function isSampleAccountId(id: string): boolean {
  return id.startsWith('asset_') || id.startsWith('liab_');
}

/** True when household accounts look like user-owned (not the bundled sample fixture). */
export function hasUserOwnedAccounts(accounts: CoreAccount[]): boolean {
  if (accounts.length === 0) return false;
  return accounts.some(
    (a) =>
      a.id.startsWith('draft_') ||
      a.id.startsWith('import_') ||
      a.id.startsWith('user_') ||
      !isSampleAccountId(a.id),
  );
}

/** Drop fixture sample investments so Wealthsimple (etc.) labels are not mixed with 401(k)/Roth demo rows. */
export function stripSampleInvestments(accounts: CoreAccount[]): CoreAccount[] {
  return accounts.filter(
    (a) => !(a.kind === 'asset' && a.isInvestment && isSampleAccountId(a.id)),
  );
}

/** Drop all bundled sample accounts (home/car/cash + demo investments + demo loans). */
export function stripSampleAccounts(accounts: CoreAccount[]): CoreAccount[] {
  return accounts.filter((a) => !isSampleAccountId(a.id));
}

/** Keep only non-sample liabilities — assets come from Mongo when synced. */
export function localLiabilitiesOnly(accounts: CoreAccount[]): CoreAccount[] {
  return accounts.filter((a) => a.kind === 'liability' && !isSampleAccountId(a.id));
}

/** Empty balance-sheet shell for signed-in users (no sample assets/liabilities). */
export function emptyUserHousehold(base?: CoreHousehold): CoreHousehold {
  const asOf = new Date();
  const asOfMonth = `${asOf.getUTCFullYear()}-${String(asOf.getUTCMonth() + 1).padStart(2, '0')}`;
  return {
    profile: {
      countryCode: base?.profile.countryCode ?? 'US',
      currency: base?.profile.currency ?? 'USD',
      householdName: 'Your household',
      monthlyIncome: base?.profile.monthlyIncome ?? 0,
      savingsRateTarget: base?.profile.savingsRateTarget ?? 0,
      asOfMonth: base?.profile.asOfMonth ?? asOfMonth,
    },
    accounts: [],
    transactions: [],
    balanceObservations: [],
  };
}

/** Build a lean CoreHousehold overlay from an onboarding draft (for derive). */
export function coreHouseholdFromDraft(
  draft: OnboardingDraft,
  base?: CoreHousehold,
): CoreHousehold {
  const asOf = new Date();
  const asOfMonth = `${asOf.getUTCFullYear()}-${String(asOf.getUTCMonth() + 1).padStart(2, '0')}`;

  return {
    profile: {
      countryCode: draft.countryCode,
      currency: draft.currencyCode,
      householdName: base?.profile.householdName ?? 'Your household',
      monthlyIncome: draft.monthlyIncomeAfterTax,
      savingsRateTarget: base?.profile.savingsRateTarget ?? 0,
      asOfMonth: base?.profile.asOfMonth ?? asOfMonth,
    },
    accounts: accountsFromDraft(draft),
    transactions: base?.transactions ?? [],
    balanceObservations: base?.balanceObservations ?? [],
  };
}

/**
 * Merge onboarding draft rows into an existing household without dropping
 * core-only liabilities (loan planner, mortgages) or imported investments.
 */
export function mergeDraftIntoHousehold(
  draft: OnboardingDraft,
  core: CoreHousehold,
): CoreHousehold {
  const fromDraft = accountsFromDraft(draft);
  const draftInvestments = fromDraft.filter((a) => a.kind === 'asset' && a.isInvestment);
  const draftNonInvest = fromDraft.filter((a) => a.kind === 'asset' && !a.isInvestment);
  const draftLiabilities = fromDraft.filter((a) => a.kind === 'liability');

  const coreNonInvest = core.accounts.filter((a) => a.kind === 'asset' && !a.isInvestment);
  const coreLiabilities = core.accounts.filter((a) => a.kind === 'liability');

  // Prefer draft investment list as-is (allows two rows both named RRSP).
  // Fall back to non-sample core investments when draft has none.
  const coreInvestments = core.accounts.filter(
    (a) => a.kind === 'asset' && a.isInvestment && !isSampleAccountId(a.id),
  );
  const investments =
    draftInvestments.length > 0 ? draftInvestments : coreInvestments;

  const nonInvest = [...draftNonInvest];
  for (const account of coreNonInvest) {
    if (isSampleAccountId(account.id)) continue;
    const key = account.label.trim().toLowerCase();
    if (!nonInvest.some((a) => a.label.trim().toLowerCase() === key)) {
      nonInvest.push(account);
    }
  }

  const liabilities = [...draftLiabilities];
  for (const account of coreLiabilities) {
    const exists = liabilities.some(
      (a) =>
        a.id === account.id ||
        (a.type === account.type &&
          a.label.trim().toLowerCase() === account.label.trim().toLowerCase()),
    );
    if (!exists) liabilities.push(account);
  }

  const accounts = [...nonInvest, ...investments, ...liabilities];

  return {
    ...core,
    profile: {
      ...core.profile,
      countryCode: draft.countryCode || core.profile.countryCode,
      currency: draft.currencyCode || core.profile.currency,
      monthlyIncome:
        draft.monthlyIncomeAfterTax > 0
          ? draft.monthlyIncomeAfterTax
          : core.profile.monthlyIncome,
      householdName:
        core.profile.householdName === 'Sample Household'
          ? 'Your household'
          : core.profile.householdName,
    },
    accounts,
    // Keep history, and baseline any investment that still has none (draft race / old import).
    balanceObservations: seedMissingInvestmentObservations(
      accounts,
      core.balanceObservations,
    ),
  };
}

/**
 * Merge imported investment accounts into an existing account list.
 * Matching by label (case-insensitive) updates balance; otherwise appends.
 */
export function mergeInvestmentAccounts(
  existing: CoreAccount[],
  imported: CoreAccount[],
): CoreAccount[] {
  const next = [...existing];
  for (const incoming of imported) {
    const matchIndex = next.findIndex(
      (a) =>
        a.kind === 'asset' &&
        a.isInvestment &&
        a.label.trim().toLowerCase() === incoming.label.trim().toLowerCase(),
    );
    if (matchIndex >= 0) {
      next[matchIndex] = {
        ...next[matchIndex],
        balance: incoming.balance,
        expectedReturnPct:
          incoming.expectedReturnPct ?? next[matchIndex].expectedReturnPct,
        type: incoming.type || next[matchIndex].type,
      };
    } else {
      next.push(incoming);
    }
  }
  return next;
}
